import { useState } from 'react';
import { PortalDsAlert, PortalDsButton, PortalDsCard, PortalDsEmptyState, PortalDsSectionHeader, portalDs } from 'src/components/portal-design-system';
import {
  useAttachPredictionMarketMutation,
  useDetachPredictionMarketMutation,
  useGetAttachedMarketsQuery,
  useReorderPredictionMarketMutation,
  useSearchPredictionMarketsMutation,
  type NormalizedMarket,
} from 'src/redux/survey/prediction-markets.endpoints';
import { MarketDisplayCard } from './market-display-card';
import { MarketSearchSheet } from './market-search-sheet';
import { MAX_ATTACHED_MARKETS } from './market-utils';

interface MarketsSectionProps {
  extSurveyId: string;
  share?: boolean;
}

export function MarketsSection({ extSurveyId, share = false }: MarketsSectionProps) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<NormalizedMarket[]>([]);
  const [partial, setPartial] = useState(false);
  const [providerErrors, setProviderErrors] = useState<string[]>([]);
  const [isSearchPending, setIsSearchPending] = useState(false);

  const {
    data: attachedMarkets = [],
    isLoading,
    isError,
    refetch,
  } = useGetAttachedMarketsQuery({ ext_survey_id: extSurveyId, share });
  const [searchMarkets, { isLoading: isSearching }] = useSearchPredictionMarketsMutation();
  const [attachMarket, { isLoading: isAttaching }] = useAttachPredictionMarketMutation();
  const [detachMarket] = useDetachPredictionMarketMutation();
  const [reorderMarket] = useReorderPredictionMarketMutation();

  const runSearch = async () => {
    setSheetOpen(true);
    setSearchError(null);
    setCandidates([]);
    setPartial(false);
    setProviderErrors([]);
    setIsSearchPending(true);
    try {
      const result = await searchMarkets({ ext_survey_id: extSurveyId, share }).unwrap();
      setCandidates(result.candidates);
      setPartial(result.partial);
      setProviderErrors(result.provider_errors ?? []);
    } catch {
      setSearchError('Market search failed. Please try again.');
    } finally {
      setIsSearchPending(false);
    }
  };

  const handleAttach = async (selected: NormalizedMarket[]) => {
    for (const market of selected) {
      await attachMarket({
        ext_survey_id: extSurveyId,
        body: {
          provider: market.provider,
          provider_market_id: market.provider_market_id,
          relevance_note: market.relevance_note,
          snapshot: market,
        },
      }).unwrap();
    }
    await refetch();
  };

  const handleReorder = async (attachmentId: string, displayOrder: number) => {
    await reorderMarket({
      ext_survey_id: extSurveyId,
      attachment_id: attachmentId,
      display_order: displayOrder,
    }).unwrap();
    await refetch();
  };

  const sortedAttachments = [...attachedMarkets].sort(
    (left, right) => left.display_order - right.display_order,
  );

  return (
    <section className="px-4 py-2 md:px-6">
      <PortalDsCard>
      <PortalDsSectionHeader
        title="Prediction markets"
        description="Attach live market-implied probabilities alongside your survey results."
        actions={(
          <div className="flex gap-2">
            {attachedMarkets.length > 0 && (
              <PortalDsButton variant="secondary" onClick={() => { void runSearch(); }}>
                Find different markets
              </PortalDsButton>
            )}
            {attachedMarkets.length < MAX_ATTACHED_MARKETS && (
              <PortalDsButton variant="accent" onClick={() => { void runSearch(); }}>
                Find related markets
              </PortalDsButton>
            )}
          </div>
        )}
      />

      {isLoading && <p className={portalDs.bodySm}>Loading attached markets…</p>}

      {isError && (
        <PortalDsAlert variant="danger" title="Could not load attached markets">
          <PortalDsButton className="mt-3" variant="secondary" size="sm" onClick={() => { void refetch(); }}>
            Retry
          </PortalDsButton>
        </PortalDsAlert>
      )}

      {!isLoading && !isError && attachedMarkets.length === 0 && (
        <PortalDsEmptyState
          title="No markets attached"
          description={`Search Kalshi and Polymarket for live markets related to this survey, then attach up to ${MAX_ATTACHED_MARKETS} to display market-implied probabilities here.`}
          action={(
            <PortalDsButton variant="accent" onClick={() => { void runSearch(); }}>
              Find related markets
            </PortalDsButton>
          )}
        />
      )}

      {sortedAttachments.length > 0 && (
        <div className="flex flex-wrap gap-4">
          {sortedAttachments.map((attachment, index) => (
            <MarketDisplayCard
              key={attachment.id}
              attachment={attachment}
              canMoveEarlier={index > 0}
              canMoveLater={index < sortedAttachments.length - 1}
              onMoveEarlier={() => {
                void handleReorder(attachment.id, index - 1);
              }}
              onMoveLater={() => {
                void handleReorder(attachment.id, index + 1);
              }}
              onRemove={() => {
                void detachMarket({
                  ext_survey_id: extSurveyId,
                  attachment_id: attachment.id,
                }).then(() => refetch());
              }}
            />
          ))}
        </div>
      )}

      <MarketSearchSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        candidates={candidates}
        partial={partial}
        providerErrors={providerErrors}
        isSearching={isSearchPending || isSearching}
        searchError={searchError}
        onRetry={() => { void runSearch(); }}
        onAttach={handleAttach}
        isAttaching={isAttaching}
        existingCount={attachedMarkets.length}
      />
      </PortalDsCard>
    </section>
  );
}
